<template>
  <div class="row mt-4" v-if="hayDatos">
    <div class="col-12">
      <div class="card ficha-listado">
        <div class="px-4 pb-4 card-body">

          <div class="mb-3">
            <h6 class="mb-0 panel-section-title">Métodos de Pago</h6>
            <p class="text-xs opacity-7 mb-0">Top métodos utilizados por tus compradores</p>
          </div>

          <div class="mp-layout">

            <!-- Gráfico agrupado EUR / USD -->
            <div class="mp-chart-col">
              <div class="mp-legend">
                <span class="mp-legend-dot" style="background:var(--q-gold)"></span>
                <span class="text-xs">EUR</span>
                <span class="mp-legend-dot" style="background:#26c6da; margin-left:10px;"></span>
                <span class="text-xs">USD</span>
              </div>
              <div class="mp-canvas-wrap">
                <canvas id="mp-chart"></canvas>
              </div>
            </div>

            <!-- Tabla resumen financiero -->
            <div class="mp-table-col">
              <table class="mp-table">
                <thead>
                  <tr>
                    <th class="mp-th-method">Método</th>
                    <th class="mp-th-cur tc-th--azul">EUR</th>
                    <th class="mp-th-cur tc-th--usd">USD</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="m in datos" :key="m.metodo">
                    <td class="mp-td-method">
                      <span class="mp-icon">{{ metodoPagoIcon(m.metodo) }}</span>
                      <span class="mp-nombre">{{ m.metodo }}</span>
                    </td>
                    <td class="mp-td-cur">
                      <template v-if="m.ventas_eur > 0">
                        <span class="mp-count-badge eur">{{ m.ventas_eur }}</span>
                        <span class="mp-amount eur">{{ fmt(m.importe_eur, 'EUR') }}</span>
                      </template>
                      <span v-else class="mp-na">—</span>
                    </td>
                    <td class="mp-td-cur">
                      <template v-if="m.ventas_usd > 0">
                        <span class="mp-count-badge usd">{{ m.ventas_usd }}</span>
                        <span class="mp-amount usd">{{ fmt(m.importe_usd, 'USD') }}</span>
                      </template>
                      <span v-else class="mp-na">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";

const ICONS = {
  'Visa':        '💳',
  'Mastercard':  '💳',
  'Amex':        '💳',
  'Discover':    '💳',
  'Unionpay':    '💳',
  'Card':        '💳',
  'Google Pay':  '🟢',
  'Apple Pay':   '🍎',
  'Samsung Pay': '📱',
  'PayPal':      '🅿️',
  'Amazon Pay':  '📦',
  'Link':        '🔗',
  'Klarna':      '🛍️',
  'SEPA Debit':  '🏦',
  'iDEAL':       '🏦',
  'Sofort':      '⚡',
  'Bancontact':  '🏦',
};

export default {
  name: 'PanelTipsterMetodosPago',
  props: {
    datos: { type: Array, default: () => [] },
  },
  data() {
    return { chartInstance: null };
  },
  computed: {
    hayDatos() { return this.datos && this.datos.length > 0; },
    topMetodos() { return this.datos.slice(0, 8); },
  },
  watch: {
    datos: { deep: true, handler() { this.$nextTick(this.renderChart); } },
  },
  mounted() {
    this.$nextTick(this.renderChart);
  },
  beforeUnmount() {
    this.chartInstance?.destroy();
  },
  methods: {
    metodoPagoIcon(m) { return ICONS[m] || '💰'; },

    fmt(n, cur) {
      return Number(n || 0).toLocaleString('es-ES', {
        style: 'currency',
        currency: cur,
        maximumFractionDigits: 0,
      });
    },

    renderChart() {
      const canvas = document.getElementById('mp-chart');
      if (!canvas || !this.topMetodos.length) return;
      this.chartInstance?.destroy();

      const labels  = this.topMetodos.map(m => m.metodo);
      const dataEur = this.topMetodos.map(m => m.ventas_eur);
      const dataUsd = this.topMetodos.map(m => m.ventas_usd);
      const amtEur  = this.topMetodos.map(m => m.importe_eur);
      const amtUsd  = this.topMetodos.map(m => m.importe_usd);

      const barH = 14;
      canvas.parentElement.style.height = `${labels.length * (barH * 2 + 16) + 40}px`;

      this.chartInstance = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'EUR',
              data: dataEur,
              backgroundColor: 'rgba(245,166,35,.85)',
              hoverBackgroundColor: 'rgba(245,166,35,1)',
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: barH,
              extra: amtEur,
            },
            {
              label: 'USD',
              data: dataUsd,
              backgroundColor: 'rgba(38,198,218,.75)',
              hoverBackgroundColor: 'rgba(38,198,218,.95)',
              borderRadius: 6,
              borderSkipped: false,
              maxBarThickness: barH,
              extra: amtUsd,
            },
          ],
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const v   = ctx.parsed.x;
                  const amt = ctx.dataset.extra?.[ctx.dataIndex] ?? 0;
                  const cur = ctx.dataset.label;
                  const fmtAmt = Number(amt).toLocaleString('es-ES', {
                    style: 'currency', currency: cur, maximumFractionDigits: 0
                  });
                  return ` ${v} transacciones · ${fmtAmt}`;
                },
              },
            },
          },
          scales: {
            x: {
              stacked: false,
              grid: { color: 'rgba(255,255,255,.06)' },
              ticks: {
                color: 'rgba(231,237,245,.5)',
                font: { size: 10 },
                precision: 0,
              },
              border: { dash: [4, 4] },
            },
            y: {
              stacked: false,
              grid: { display: false },
              ticks: {
                color: 'rgba(231,237,245,.75)',
                font: { size: 11, weight: '600' },
              },
            },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.mp-layout {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}

.mp-chart-col {
  flex: 1 1 300px;
  min-width: 260px;
}

.mp-legend {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 8px;
  font-size: 11px;
  color: var(--q-muted);
}
.mp-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.mp-canvas-wrap {
  position: relative;
  width: 100%;
  min-height: 120px;
}
.mp-canvas-wrap canvas { width: 100% !important; height: 100% !important; }

.mp-table-col {
  flex: 0 0 auto;
  min-width: 280px;
}

.mp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.mp-table thead th {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
  padding: 4px 8px;
  text-align: right;
  color: var(--q-muted);
  border-bottom: 1px solid var(--q-border);
}
.mp-th-method { text-align: left; }
.tc-th--azul { color: var(--q-gold) !important; }
.tc-th--usd  { color: #26c6da !important; }

.mp-table tbody td {
  padding: 7px 8px;
  border-bottom: 1px solid var(--q-border);
  vertical-align: middle;
}
.mp-td-method {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--q-text);
  white-space: nowrap;
}
.mp-icon { font-size: 13px; flex-shrink: 0; }
.mp-nombre { font-size: 12px; }

.mp-td-cur {
  text-align: right;
  white-space: nowrap;
}
.mp-count-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 20px;
  margin-right: 4px;
  font-variant-numeric: tabular-nums;
}
.mp-count-badge.eur { background: rgba(245,166,35,.18); color: var(--q-gold); }
.mp-count-badge.usd { background: rgba(38,198,218,.15); color: #26c6da; }

.mp-amount {
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-family: var(--q-font-mono);
}
.mp-amount.eur { color: var(--q-gold); }
.mp-amount.usd { color: #26c6da; }
.mp-na { color: var(--q-muted); font-size: 12px; }

@media (max-width: 768px) {
  .mp-layout { flex-direction: column; gap: 20px; }
  .mp-table-col { min-width: unset; width: 100%; }
}
</style>
